import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Users, Star, IndianRupee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('location') || '');
  const [guests, setGuests] = useState(searchParams.get('guests') || 2);

  useEffect(() => {
    fetchProperties();
  }, [searchParams]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const location = searchParams.get('location') || '';
      const guestsParam = searchParams.get('guests') || '';
      
      const response = await axios.get(`${BACKEND_URL}/api/properties`, {
        params: {
          location,
          guests: guestsParam
        }
      });
      
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to fetch properties');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    navigate(`/properties?location=${searchQuery}&guests=${guests}`);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            >
              <Logo size="md" />
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/host-dashboard')}
                data-testid="nav-host-dashboard"
              >
                Become a Host
              </Button>
              <Button 
                className="rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white px-6"
                onClick={() => navigate('/login')}
                data-testid="nav-login"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-8" data-testid="search-filters">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full">
              <MapPin className="h-5 w-5 text-[#4A4A4A]" />
              <Input 
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 focus-visible:ring-0"
                data-testid="filter-location-input"
              />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full">
              <Users className="h-5 w-5 text-[#4A4A4A]" />
              <Input 
                type="number"
                min="1"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                className="border-0 focus-visible:ring-0 w-20"
                data-testid="filter-guests-input"
              />
              <span className="text-sm text-[#4A4A4A]">guests</span>
            </div>
            <Button 
              className="rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white px-8"
              onClick={handleSearch}
              data-testid="search-btn"
            >
              <Search className="h-5 w-5 mr-2" />
              Search
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="loading-spinner">
            <div className="w-16 h-16 border-4 border-[#8B2E2E] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div>
            <h2 
              className="text-2xl font-bold mb-6 text-[#4A4A4A]"
              style={{fontFamily: 'Playfair Display, serif'}}
              data-testid="results-title"
            >
              {properties.length} Properties Found
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="property-grid">
              {properties.map((property, idx) => (
                <div 
                  key={property.property_id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm property-card cursor-pointer"
                  onClick={() => navigate(`/properties/${property.property_id}`)}
                  data-testid={`property-card-${idx}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={property.images[0] || 'https://images.pexels.com/photos/31790464/pexels-photo-31790464.jpeg'}
                      alt={property.title}
                      className="w-full h-full object-cover property-image"
                    />
                  </div>
                  
                  <div className="p-6">
                    <h3 
                      className="text-xl font-bold mb-2 text-[#4A4A4A]"
                      style={{fontFamily: 'Playfair Display, serif'}}
                    >
                      {property.title}
                    </h3>
                    
                    <p className="text-sm text-[#4A4A4A]/70 mb-3">
                      {property.location.city}, {property.location.country}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <IndianRupee className="h-5 w-5 text-[#8B2E2E]" />
                        <span className="text-lg font-bold text-[#4A4A4A]" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                          {property.price_per_night.toLocaleString()}
                        </span>
                        <span className="text-sm text-[#4A4A4A]/70">/night</span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-[#4A4A4A]/70" />
                        <span className="text-sm text-[#4A4A4A]/70">{property.max_guests}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {properties.length === 0 && (
              <div className="text-center py-20" data-testid="no-results">
                <Logo variant="icon" size="xl" className="mx-auto mb-4 opacity-30" />
                <h3 className="text-xl font-bold text-[#4A4A4A] mb-2">No properties found</h3>
                <p className="text-[#4A4A4A]/70">Try adjusting your search filters</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyList;
